import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import { RoofTypeForm, RoofTypeDetailResponse, RoofTypeListResponse, RoofTypeUpdate } from "../types";
import {
  selectRoofTypeForm,
  selectRoofTypePage,
  selectRoofTypePerPage,
} from "./selector";
import { roofTypeActions } from "./slice";

let isFetchingList = false;

export function* handleGetRoofTypeList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectRoofTypePage);
    const perPage: number = yield select(selectRoofTypePerPage);

    const response: RoofTypeListResponse = yield call(
      request,
      `${API_URL}/roof-beam-type/list?page=${page}&perPage=${perPage}`,
      { method: "GET" },
    );

    const rawData = response?.data || [];
    const meta = getPaginationMeta(page, perPage, rawData, response?.meta);
    const data = rawData.filter((item) =>
      Boolean(String(item?.beamTypeDesc ?? "").trim()),
    );
    const payload = { data, meta };

    if (page === 1) {
      yield put(roofTypeActions.getRoofTypeListSuccess(payload));
    } else {
      yield put(roofTypeActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectRoofTypePage);

    if (page === 1) {
      yield put(roofTypeActions.getRoofTypeListError(message));
    } else {
      yield put(roofTypeActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddRoofType() {
  const form: RoofTypeForm = yield select(selectRoofTypeForm);

  try {
    yield call(request, `${API_URL}/roof-beam-type`, {
      method: "POST",
      body: JSON.stringify({
        beamTypeDesc: form.description.trim(),
        beamMaxHeight: Number(form.maxHeight),
      }),
    });

    yield put(roofTypeActions.addRoofTypeSuccess());
    yield put(roofTypeActions.getRoofTypeList());
  } catch (error) {
    yield put(roofTypeActions.addRoofTypeError((error as Error).message));
  }
}

export function* handleUpdateRoofType(action: PayloadAction<RoofTypeUpdate>) {
  const { id, beamTypeDesc, beamMaxHeight } = action.payload;

  try {
    yield call(request, `${API_URL}/roof-beam-type/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        beamTypeDesc: beamTypeDesc.trim(),
        beamMaxHeight: Number(beamMaxHeight),
      }),
    });

    yield put(roofTypeActions.updateRoofTypeSuccess());
    yield put(roofTypeActions.getRoofTypeList());
  } catch (error) {
    yield put(roofTypeActions.updateRoofTypeError((error as Error).message));
  }
}

export function* handleGetRoofTypeDetail(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    const response: RoofTypeDetailResponse = yield call(
      request,
      `${API_URL}/roof-beam-type/${id}`,
      { method: "GET" },
    );

    yield put(roofTypeActions.getRoofTypeDetailSuccess(response?.data));
  } catch (error) {
    yield put(roofTypeActions.getRoofTypeDetailError((error as Error).message));
  }
}

export function* handleDeleteRoofType(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/roof-beam-type/${id}`, {
      method: "DELETE",
    });

    yield put(roofTypeActions.deleteRoofTypeSuccess(id));
  } catch (error) {
    yield put(roofTypeActions.deleteRoofTypeError((error as Error).message));
  }
}

export function* roofTypeSaga() {
  yield takeLeading(
    roofTypeActions.getRoofTypeList.type,
    handleGetRoofTypeList
  );
  yield takeLeading(
    roofTypeActions.getNextPageItems.type,
    handleGetRoofTypeList
  );
  yield takeLatest(roofTypeActions.addRoofType.type, handleAddRoofType);
  yield takeLatest(roofTypeActions.updateRoofType.type, handleUpdateRoofType);
  yield takeLatest(
    roofTypeActions.getRoofTypeDetail.type,
    handleGetRoofTypeDetail,
  );
  yield takeLatest(roofTypeActions.deleteRoofType.type, handleDeleteRoofType);
}

export default roofTypeSaga;
